import { useEffect, useMemo, useState } from "react";
import { useUserProvider } from "./providers/UserProvider";
import { useAuth } from "react-oauth2-pkce";
import jwtDecode from "jwt-decode";

import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";

import RequireAccess from "./components/RequireAccess";
import AppLayout from "./components/AppLayout";
import Home from "./pages/Home";
import AppSelector from "./pages/AppSelector";
import About from "./pages/About";
import AboutApp from "./pages/AboutApp";
import PushNotification from "./pages/PushNotification";

import WelcomeScreenList from "./pages/WelcomeScreenList";
import WelcomeScreenDetail from "./pages/WelcomeScreenDetail";

import FlexContentList from "./pages/FlexContentList";
import NewApp from "./pages/NewApp";
import OnboardingAppSelector from "./pages/OnboardingAppSelector";

import WhatsNewList from "./pages/WhatsNewList";
import WhatsNewDetail from "./pages/WhatsNewDetail";
import BusinessList from "./pages/BusinessList";

import AboutAppMembers from "./pages/AboutAppMembers";

import AppSetup from "./pages/AppSetup";

import ProfilesList from "./pages/ProfilesList";
import ProfilesDetail from "./pages/ProfilesDetail";

import MemberList from "./pages/MemberList";
import MemberDetail from "./pages/MemberDetail";

import UserList from "./pages/UserList";
import UserDetail from "./pages/UserDetail";

import CreatorDetail from "./pages/CreatorDetail";

import TagList from "./pages/TagList";
import TagDetail from "./pages/TagDetail";

import DirectoryList from "./pages/DirectoryList";
import DirectoryDetail from "./pages/DirectoryDetail";

import TopicList from "./pages/TopicList";
import TopicDetail from "./pages/TopicDetail";
import AppInfo from "./pages/AppInfo";

import VirtualEventList from "./pages/VirtualEventList";
import VirtualEventDetail from "./pages/VirtualEventDetail";

import ContentList from "./pages/ContentList";
import ContentDetail from "./pages/ContentDetail";

import CourseCategoriesList from "./pages/CourseCategoriesList";
import CourseCategoryDetail from "./pages/CourseCategoryDetail";

import CourseList from "./pages/CourseList";
import CourseDetail from "./pages/CourseDetail";

import CourseModuleDetail from "./pages/CourseModuleDetail";

import OffersList from "./pages/OffersList";
import OffersDetail from "./pages/OffersDetail";

import CourseLessonList from "./pages/CourseLessonList";
import CourseLessonDetail from "./pages/CourseLessonDetail";

import EventCalendarList from "./pages/EventCalendarList";
import EventCalendarDetail from "./pages/EventCalendarDetail";

import TrackDetails from "./pages/TrackDetails";
import TrackList from "./pages/TrackList";

import TrackNowScreen from "./pages/TrackNowScreen";

import MessagesDetails from "./pages/MessagesDetails";
import MessagesList from "./pages/MessagesList";

import UnlinkMedia from "./pages/UnlinkMedia";
import RemovedApps from "./pages/RemovedApps";

import { GetApp, GetAllAvailableApps } from "./services/api/app";

import { useLoaderProvider } from "./providers/LoaderProvider";
import useLocalStorage from "./lib/useLocalStorage";
import ChangePassword from "./pages/ChangePassword";
import AppBuilding from "./pages/AppBuilding";
import SuperUserList from "./pages/SuperUserList";
import SuperUserDetails from "./pages/SuperUserDetails";
import Records from "./pages/Records";
import WebApp from "./pages/WebApp";
import { useModalProvider } from "./providers/ModalProvider";
import { InvalidAccountTypeError } from "./pages/Login";
import MenuSetup from "./pages/MenuSetup";
import DomainSetup from "./pages/DomainSetup";
import ActionFormsDetail from "./pages/ActionFormDetail";
import { AccountStatus } from "./pages/AccountStatus";
import AccountStatusList from "./pages/AccountStatusList";
import GlobalFieldsDetail from "./pages/GlobalFieldsDetail";
import GlobalFieldsList from "./pages/GlobalFieldsList";
import OnboardingTracker from "./pages/OnboardingTracker";
import ActionFormList from "./pages/ActionFormList";
import PushNotificationList from "./pages/PushNotificationList";
import AppDownloadsList from "./pages/AppDownloadsList";
import PushSubscribersList from "./pages/PushSubscribersList";
import Components from "./pages/Components";
import Onboarding from "./pages/Onboarding";
import OnboardingChangePassword from "./pages/OnboardingChangePassword";
import { useOnboardingProvider } from "./providers/OnboardingProvider";
import BusinessDetail from "./pages/BusinessDetail";
import PortalHomeScreen from "./pages/PortalHomeScreen";
import FreeAccountMessage from "./pages/FreeAccountMessage";
import CreatorList from "./pages/CreatorList";
import FirstLoginModal from "./components/modal_contents/FirstLoginModal/FirstLoginModal";
import { LAUNCHED } from "./lib/onboarding_config/config";
import GoToOnboarding from "./pages/GoToOnboarding";
import LoginPending from "./pages/LoginPending";
import CategoryDetail from "./pages/CategoryDetail";
import Categories from "./pages/Categories";
import CustomCTAList from "./pages/CustomCTAList";
import CustomCTADetail from "./pages/CustomCTADetail";
import FlexContentDetail from "./pages/FlexContentDetail";

export const setAppData = ({
  idToken,
  data,
  isSuper,
  currentApp,
  app,
  setUser,
  setOnboardingData,
  postCallback,
  setIsAppChanged,
  isAppChanged,
}) => {
  GetApp({ idToken, currentApp })
    .then((res) => res.json())
    .then((res) => {
      let app_data = {};

      app_data = res.data;

      delete app_data.member_content_message;
      delete app_data.id;

      setUser({ idToken, data, isSuper, currentApp, app, app_data });
      if (typeof setOnboardingData === "function")
        setOnboardingData(res?.data?.onboarding);
      postCallback();
    })
    .finally(() => {
      if (typeof setIsAppChanged === "function" && isAppChanged === false)
        setIsAppChanged(true);
    });
};

const ProtectedRoute = ({ component: Component, render, ...rest }) => {
  const { needsOnboarding } = useOnboardingProvider();
  const { user } = useUserProvider();

  if (needsOnboarding === null || !user) {
    return <LoginPending />
  }

  return (
    <Route
      {...rest}
      render={(props) => {
        if (needsOnboarding && !rest.path.includes('/onboarding')) {
          return <Redirect to="/onboarding" />;
        }

        // force redirection only happens for non super admins
        if (!needsOnboarding && rest?.path.includes('/onboarding') && !user?.isSuper) {
          return <Redirect to="/" />;
        }

        if (Component) {
          return <Component {...props} />;
        }

        if (render) {
          return render(props);
        }

        // If neither component nor render is provided, return null or some default component
        return null;
      }}
    />
  );
};

function App() {
  const { authService } = useAuth();
  const { user, setUser } = useUserProvider();
  const { showModal } = useModalProvider();
  const { doSetShowLoadingModal } = useLoaderProvider();
  const { setOnboardingData, onboardingData, needsOnboarding } = useOnboardingProvider();
  const isOnboardingPage = window.location.pathname.includes("/onboarding");
  const onboardingStatus = user?.app_data?.onboarding?.status;


  // eslint-disable-next-line no-unused-vars
  const [currentSelectedApp, setCurrentSelectedApp] = useLocalStorage(
    "currentApp",
    null
  );

  const isPNAllowed = useMemo(
    () =>
      user?.app_data?.from_apps?.length > 0 ||
      (user?.app_data?.fcm_key?.length > 0 &&
        user?.app_data?.fcm_project_id?.length > 0),
    [user]
  );

  useEffect(() => {
    doSetShowLoadingModal(true);
    const idToken = authService.getAuthTokens().id_token;
    const data = jwtDecode(idToken);

    GetAllAvailableApps(idToken)
      .then((res) => res.json())
      .then((res) => {
        if (res.data.length === 0) {
          doSetShowLoadingModal(false);
          return showModal(<InvalidAccountTypeError />);
        }

        const app =
          currentSelectedApp &&
          res.data.some((a) => a.id === currentSelectedApp.id)
            ? currentSelectedApp
            : res.data[0];
        const currentApp = () => app.id;
        const isSuper = data.is_super;

        if (idToken !== undefined) {
          setAppData({
            idToken,
            data,
            isSuper,
            currentApp,
            app,
            setUser,
            setOnboardingData,
            postCallback: () => {
              doSetShowLoadingModal(false);
            },
          });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFirstLoginModalPrompted = sessionStorage.getItem("first_login_prompted") === "true";
  const isLoggedIn = useMemo(() => authService.isAuthenticated() && !authService.isPending(), [authService]);
  const isLaunched = useMemo(() => onboardingStatus === LAUNCHED, [onboardingStatus])

  const shouldShowFirstLoginModal = useMemo(() => {
    return isLoggedIn && !user?.isSuper && isLaunched && !isOnboardingPage &&
      onboardingData?.confetti === true && !isFirstLoginModalPrompted
  }, [
    isLoggedIn,
    isLaunched,
    isOnboardingPage,
    user?.isSuper,
    isFirstLoginModalPrompted,
    onboardingData?.confetti
  ])

  useEffect(() => {
    let isMounted = true

    if (isMounted && shouldShowFirstLoginModal) {
      isMounted = false;

      setTimeout(() => {
        showModal(<FirstLoginModal onboardingData={onboardingData} user={user} />)
      }, 500)
    }

    return () => {
      isMounted = true
    }
  }, [shouldShowFirstLoginModal])

  const app = () => (
    <Router>
      <AppLayout>
      <Switch>
        <ProtectedRoute path="/_/components" component={Components} />
        
        <ProtectedRoute 
          path="/gotoonboarding/:uuid" 
          render={(props) => <GoToOnboarding {...props}/>} 
        />

        <ProtectedRoute path="/onboarding/change_password" component={OnboardingChangePassword} />

        <ProtectedRoute path="/onboarding/change_application" component={OnboardingAppSelector} />
        <ProtectedRoute path="/onboarding" component={Onboarding} />
        
        <ProtectedRoute 
          path="/creators/new" 
          render={(props) => <CreatorDetail newCreator={true} />} 
        />
        
        <ProtectedRoute 
          path="/creators/:uuid" 
          render={(props) => <CreatorDetail {...props} />} 
        />
        
        <ProtectedRoute path="/creators" component={CreatorList} />

        <ProtectedRoute path="/about" component={About} />
        
        <ProtectedRoute path="/change_application" component={AppSelector} />
        
        <ProtectedRoute path="/change_password" component={ChangePassword} />
        
        <ProtectedRoute 
          path="/app/about"
          render={() => (
            <RequireAccess requiredAccess={["super"]}>
              <AboutApp />
            </RequireAccess>
          )}
        />

      <ProtectedRoute
            path="/custom_cta/:uuid"
            render={(props) => (
                <CustomCTADetail {...props}/>
            )}
          />
          <ProtectedRoute
            path="/custom_cta"
            render={() => (
           
                <CustomCTAList />
            )}
          />

        <ProtectedRoute 
          path="/push_notifications/new"
          render={() => (
            <RequireAccess
              requiredAccess={["premium_plus", "push_notif_keys", "versionizer"]}
              maxVersion={8.5}
              hasMetOtherChecker={isPNAllowed}
            >
              <PushNotification />
            </RequireAccess>
          )}
        />

        <ProtectedRoute 
          path="/app/new"
          render={() => (
            <RequireAccess requiredAccess={["super"]}>
              <NewApp />
            </RequireAccess>
          )}
        />

        <ProtectedRoute 
          path="/app/setup"
          render={() => (
            <RequireAccess requiredAccess={["super"]}>
              <AppSetup />
            </RequireAccess>
          )}
        />

        <ProtectedRoute 
          path="/app/building"
          render={() => (
            <RequireAccess requiredAccess={["super"]}>
              <AppBuilding />
            </RequireAccess>
          )}
        />

        <ProtectedRoute 
          path="/app/domains"
          render={() => (
            <RequireAccess requiredAccess={["super"]}>
              <DomainSetup />
            </RequireAccess>
          )}
        />

        <ProtectedRoute 
          path="/welcome_screens/new" 
          render={(props) => <WelcomeScreenDetail newWelcomeScreen={true} />} 
        />
        
        <ProtectedRoute path="/portal_homescreen" component={PortalHomeScreen} />

        <ProtectedRoute path="/free_account_message" component={FreeAccountMessage} />
        
        <ProtectedRoute 
          path="/welcome_screens/:uuid" 
          render={(props) => <WelcomeScreenDetail {...props} />} 
        />
        
        <ProtectedRoute path="/welcome_screens" component={WelcomeScreenList} />

        <ProtectedRoute 
          path="/app/about_members"
          render={() => (
            <RequireAccess requiredAccess={["premium_plus"]}>
              <AboutAppMembers />
            </RequireAccess>
          )}
        />

        <ProtectedRoute 
          path="/profiles/new" 
          render={(props) => <ProfilesDetail newProfile={true} />} 
        />
        
        <ProtectedRoute 
          path="/profiles/:uuid" 
          render={(props) => <ProfilesDetail {...props} />} 
        />
        
        <ProtectedRoute path="/profiles" component={ProfilesList} />

        <ProtectedRoute 
          path="/podcasts/new"
          render={() => (
            <RequireAccess
              requiredAccess={["versionizer"]}
              minVersion={9}
              hasMetOtherChecker={user?.app_data?.setup?.podcasts === true}
            >
              <ContentDetail
                isContent={true}
                contentType="podcasts"
                newContent={true}
              />
            </RequireAccess>
          )}
        />

        <ProtectedRoute
          path="/categories/new"
          render={(props) => {
            return <CategoryDetail newCategory={true} />;
          }}
        />

        <ProtectedRoute
          path="/categories/:uuid"
          render={(props) => {
            return <CategoryDetail {...props} />;
          }}
        />

        <ProtectedRoute 
          path="/categories"
          render={() => (
            <RequireAccess
              requiredAccess={["versionizer"]}
              minVersion={10}
            >
              <Categories />
            </RequireAccess>
          )}
        />


        <ProtectedRoute 
          path="/podcasts/:uuid"
          render={() => (
            <RequireAccess
              requiredAccess={["versionizer"]}
              minVersion={9}
              hasMetOtherChecker={user?.app_data?.setup?.podcasts === true}
            >
              <ContentDetail isContent={true} contentType="podcasts" />
            </RequireAccess>
          )}
        />

        <ProtectedRoute 
          path="/podcasts"
          render={() => (
            <RequireAccess
              requiredAccess={["versionizer"]}
              minVersion={9}
              hasMetOtherChecker={user?.app_data?.setup?.podcasts === true}
            >
              <ContentList contentType="podcasts" />
            </RequireAccess>
          )}
        />

        <ProtectedRoute 
          path="/documents/new" 
          render={() => (
            <ContentDetail
              isContent={true}
              contentType="documents"
              newContent={true}
            />
          )}
        />

        <ProtectedRoute 
          path="/documents/:uuid" 
          render={() => <ContentDetail isContent={true} contentType="documents" />}
        />

        <ProtectedRoute 
          path="/documents" 
          render={() => <ContentList contentType="documents" />}
        />

        <ProtectedRoute
          path="/members/new"
          render={(props) => {
            return <MemberDetail newUser={true} />;
          }}
        />
        <ProtectedRoute
          path="/members/:uuid"
          render={(props) => {
            return <MemberDetail {...props} />;
          }}
        />
        <ProtectedRoute path="/members">
          <MemberList />
        </ProtectedRoute>
        <ProtectedRoute
          path="/business/:uuid"
          render={(props) => {
            return <BusinessDetail {...props} />;
          }}
        />
        <ProtectedRoute path="/business">
          <BusinessList />
        </ProtectedRoute>

        <ProtectedRoute
          path="/tags/new"
          render={(props) => {
            return <TagDetail newTag={true} />;
          }}
        />
        <ProtectedRoute
          path="/tags/:uuid"
          render={(props) => {
            return <TagDetail {...props} />;
          }}
        />
        <ProtectedRoute path="/tags">
          <TagList />
        </ProtectedRoute>

        <ProtectedRoute
          path="/directories/new"
          render={(props) => {
            return <DirectoryDetail newDirectory={true} />;
          }}
        />
        <ProtectedRoute
          path="/directories/:uuid"
          render={(props) => {
            return <DirectoryDetail {...props} />;
          }}
        />
        <ProtectedRoute path="/directories">
          <DirectoryList />
        </ProtectedRoute>

        <ProtectedRoute
          path="/videos/new"
          render={(props) => {
            return (
              <ContentDetail
                isContent={true}
                contentType="videos"
                newContent={true}
              />
            );
          }}
        />
        <ProtectedRoute path="/videos/:uuid">
          <ContentDetail isContent={true} contentType="videos" />
        </ProtectedRoute>
        <ProtectedRoute path="/videos">
          <ContentList contentType="videos" />
        </ProtectedRoute>

        <ProtectedRoute
          path="/articles/new"
          render={(props) => {
            return (
              <ContentDetail
                isContent={true}
                contentType="articles"
                newContent={true}
              />
            );
          }}
        />
        <ProtectedRoute path="/articles/:uuid">
          <ContentDetail isContent={true} contentType="articles" />
        </ProtectedRoute>
        <ProtectedRoute path="/articles">
          <ContentList contentType="articles" />
        </ProtectedRoute>

        <ProtectedRoute
          path="/audios/new"
          render={(props) => {
            return (
              <ContentDetail
                isContent={true}
                contentType="audios"
                newContent={true}
              />
            );
          }}
        />
        <ProtectedRoute path="/audios/:uuid">
          <ContentDetail isContent={true} contentType="audios" />
        </ProtectedRoute>
        <ProtectedRoute path="/audios">
          <ContentList contentType="audios" />
        </ProtectedRoute>

        <ProtectedRoute
          path="/galleries/new"
          render={(props) => {
            return (
              <ContentDetail
                isContent={true}
                contentType="galleries"
                newContent={true}
              />
            );
          }}
        />
        <ProtectedRoute path="/galleries/:uuid">
          <ContentDetail isContent={true} contentType="galleries" />
        </ProtectedRoute>
        <ProtectedRoute path="/galleries">
          <ContentList contentType="galleries" />
        </ProtectedRoute>

        <ProtectedRoute
          path="/journals/new"
          render={(props) => {
            return (
              <ContentDetail
                isContent={true}
                contentType="journals"
                newContent={true}
              />
            );
          }}
        />
        <ProtectedRoute path="/journals/:uuid">
          <ContentDetail isContent={true} contentType="journals" />
        </ProtectedRoute>
        <ProtectedRoute path="/journals">
          <ContentList contentType="journals" />
        </ProtectedRoute>

        <ProtectedRoute
          path="/lessons/new"
          render={(props) => {
            return (
              <ContentDetail
                isContent={true}
                contentType="lessons"
                newContent={true}
              />
            );
          }}
        />
        <ProtectedRoute path="/lessons/:uuid">
          <ContentDetail isContent={true} contentType="lessons" />
        </ProtectedRoute>
        <ProtectedRoute path="/lessons">
          <ContentList contentType="lessons" />
        </ProtectedRoute>

        <ProtectedRoute
          path="/offers/new"
          render={(props) => {
            return <OffersDetail newOffer={true} />;
          }}
        />
        <ProtectedRoute
          path="/offers/:uuid"
          render={(props) => {
            return <OffersDetail {...props} />;
          }}
        />
        <ProtectedRoute path="/offers">
          <OffersList />
        </ProtectedRoute>

        <ProtectedRoute
          path="/products/new"
          render={(props) => {
            return (
              <ContentDetail
                isContent={true}
                contentType="products"
                newContent={true}
              />
            );
          }}
        />
        <ProtectedRoute path="/products/:uuid">
          <ContentDetail isContent={true} contentType="products" />
        </ProtectedRoute>
        <ProtectedRoute path="/products">
          <ContentList contentType="products" />
        </ProtectedRoute>

        <ProtectedRoute
          path="/resources/new"
          render={(props) => {
            return (
              <ContentDetail
                isContent={true}
                contentType="resources"
                newContent={true}
              />
            );
          }}
        />
        <ProtectedRoute path="/resources/:uuid">
          <ContentDetail isContent={true} contentType="resources" />
        </ProtectedRoute>
        <ProtectedRoute path="/resources">
          <ContentList contentType="resources" />
        </ProtectedRoute>

        <ProtectedRoute
          path="/questions/new"
          render={(props) => {
            return (
              <ContentDetail
                isContent={true}
                contentType="questions"
                newContent={true}
              />
            );
          }}
        />
        <ProtectedRoute path="/questions/:uuid">
          <ContentDetail isContent={true} contentType="questions" />
        </ProtectedRoute>
        <ProtectedRoute path="/questions">
          <ContentList contentType="questions" />
        </ProtectedRoute>

        <ProtectedRoute
          path="/summits/new"
          render={(props) => {
            return (
              <ContentDetail
                isContent={false}
                contentType="summits"
                newContent={true}
              />
            );
          }}
        />
        <ProtectedRoute path="/summits/:uuid">
          <ContentDetail isContent={false} contentType="summits" />
        </ProtectedRoute>
        <ProtectedRoute path="/summits">
          <ContentList isContent={false} contentType="summits" />
        </ProtectedRoute>

        <ProtectedRoute
          path="/items/new"
          render={(props) => {
            return (
              <ContentDetail
                isContent={false}
                contentType="items"
                newContent={true}
              />
            );
          }}
        />
        <ProtectedRoute path="/items/:uuid">
          <ContentDetail isContent={false} contentType="items" />
        </ProtectedRoute>
        <ProtectedRoute path="/items">
          <ContentList contentType="items" />
        </ProtectedRoute>

        <ProtectedRoute path="/track_now_screen">
          <TrackNowScreen />
        </ProtectedRoute>

        <ProtectedRoute
          path="/tips/new"
          render={(props) => {
            return (
              <ContentDetail
                isContent={true}
                contentType="tips"
                newContent={true}
              />
            );
          }}
        />
        <ProtectedRoute path="/tips/:uuid">
          <ContentDetail isContent={true} contentType="tips" />
        </ProtectedRoute>
        <ProtectedRoute path="/tips">
          <ContentList contentType="tips" />
        </ProtectedRoute>

        <ProtectedRoute
          path="/topics/new"
          render={(props) => {
            return <TopicDetail newTopic={true} />;
          }}
        />
        <ProtectedRoute
          path="/topics/:uuid"
          render={(props) => {
            return <TopicDetail {...props} />;
          }}
        />
        <ProtectedRoute path="/topics">
          <TopicList />
        </ProtectedRoute>

        <ProtectedRoute
          path="/virtual_events/new"
          render={(props) => {
            return <VirtualEventDetail newVirtualEvent={true} />;
          }}
        />
        <ProtectedRoute
          path="/virtual_events/:uuid"
          render={(props) => {
            return <VirtualEventDetail {...props} />;
          }}
        />
        <ProtectedRoute path="/virtual_events">
          <VirtualEventList />
        </ProtectedRoute>

        <ProtectedRoute
          path="/events/new"
          render={(props) => {
            return <EventCalendarDetail newEvent={true} />;
          }}
        />
        <ProtectedRoute
          path="/events/:uuid"
          render={(props) => {
            return <EventCalendarDetail {...props} />;
          }}
        />
        <ProtectedRoute path="/events">
          <EventCalendarList />
        </ProtectedRoute>

        <ProtectedRoute
          path="/course_categories/new"
          render={(props) => {
            return <CourseCategoryDetail newCategory={true} />;
          }}
        />
        <ProtectedRoute
          path="/course_categories/:uuid"
          render={(props) => {
            return <CourseCategoryDetail {...props} />;
          }}
        />
        <ProtectedRoute path="/course_categories">
          <CourseCategoriesList />
        </ProtectedRoute>

        <ProtectedRoute
          path="/courses/:course_uuid/modules/:module_uuid/lessons/new"
          render={(props) => {
            return (
              <CourseLessonDetail newCourseLesson={true} {...props} />
            );
          }}
        />
        <ProtectedRoute
          path="/courses/:course_uuid/modules/:module_uuid/lessons/:uuid"
          render={(props) => {
            return <CourseLessonDetail {...props} />;
          }}
        />

        <ProtectedRoute
          path="/courses/:course_uuid/modules/new"
          render={(props) => {
            return <CourseModuleDetail newModule={true} {...props} />;
          }}
        />

        <ProtectedRoute
          path="/flex-content/new"
          render={(props) => {
            return <FlexContentDetail {...props} newFlexContent={true}  />;
          }}
        />

         <ProtectedRoute
          path="/flex-content/:uuid"
          render={(props) => {
            return <FlexContentDetail {...props} />;
          }}
        />

        <ProtectedRoute
          path="/flex-content"
          minVersion={10}
          render={(props) => {
            return  (
              <RequireAccess
                  requiredAccess={["versionizer"]}
                  minVersion={10}
                >
                  <FlexContentList />
                </RequireAccess>
            )
            
          }}
        />

        <ProtectedRoute
          path="/courses/:course_uuid/modules/:uuid"
          render={(props) => {
            return <CourseModuleDetail {...props} />;
          }}
        />

        <ProtectedRoute
          path="/courses/new"
          render={(props) => {
            return <CourseDetail newCourse={true} />;
          }}
        />
        <ProtectedRoute
          path="/courses/:uuid"
          render={(props) => {
            return <CourseDetail {...props} />;
          }}
        />

        <ProtectedRoute path="/courses">
          <CourseList />
        </ProtectedRoute>

        <ProtectedRoute
          path="/tracks/:uuid"
          render={(props) => {
            return <TrackDetails {...props} />;
          }}
        />
        <ProtectedRoute path="/tracks">
          <TrackList />
        </ProtectedRoute>

        <ProtectedRoute
          path="/whats_new/new"
          render={(props) => {
            return (
              <RequireAccess requiredAccess={["super"]}>
                <WhatsNewDetail newWhatsNew={true} />
              </RequireAccess>
            );
          }}
        />
        <ProtectedRoute
          path="/whats_new/:uuid"
          render={(props) => {
            return (
              <RequireAccess requiredAccess={["super"]}>
                <WhatsNewDetail {...props} />
              </RequireAccess>
            );
          }}
        />
        <ProtectedRoute path="/whats_new">
          <RequireAccess requiredAccess={["super"]}>
            <WhatsNewList />
          </RequireAccess>
        </ProtectedRoute>

        <ProtectedRoute
          path="/messages/new"
          render={(props) => {
            return (
              <ContentDetail
                isContent={true}
                contentType="messages"
                newContent={true}
              />
            );
          }}
        />
        <ProtectedRoute path="/messages/:uuid">
          <ContentDetail isContent={true} contentType="messages" />
        </ProtectedRoute>
        <ProtectedRoute path="/messages">
          <ContentList contentType="messages" />
        </ProtectedRoute>

        <ProtectedRoute
          path="/users/:uuid"
          render={(props) => {
            return <UserDetail {...props} />;
          }}
        />
        <ProtectedRoute path="/users">
          <UserList />
        </ProtectedRoute>

        <ProtectedRoute
          path="/super-users/:uuid"
          render={(props) => {
            return <SuperUserDetails {...props} />;
          }}
        />

        <ProtectedRoute path="/super-users">
          <SuperUserList />
        </ProtectedRoute>
        <ProtectedRoute path="/import-export">
          <Records />
        </ProtectedRoute>

        <ProtectedRoute path="/webapp">
          <WebApp />
        </ProtectedRoute>

        <ProtectedRoute
          path="/action-lists/new"
          render={(props) => {
            return (
              <RequireAccess
                requiredAccess={["versionizer"]}
                minVersion={8.5}
              >
                <ContentDetail
                  isContent={true}
                  contentType="action-lists"
                  newContent={true}
                />
              </RequireAccess>
            );
          }}
        />
        <ProtectedRoute path="/action-lists/:uuid">
          <RequireAccess
            requiredAccess={["versionizer"]}
            minVersion={8.5}
          >
            <ContentDetail isContent={true} contentType="action-lists" />
          </RequireAccess>
        </ProtectedRoute>
        <ProtectedRoute path="/action-lists">
          <RequireAccess
            requiredAccess={["versionizer"]}
            minVersion={8.5}
          >
            <ContentList contentType="action-lists" />
          </RequireAccess>
        </ProtectedRoute>

        <ProtectedRoute path="/menu-setup">
          <MenuSetup />
        </ProtectedRoute>
        <ProtectedRoute path="/app-info">
          <AppInfo />
        </ProtectedRoute>

        <ProtectedRoute path="/unlink-media">
          <UnlinkMedia />
        </ProtectedRoute>

        <ProtectedRoute path="/removed_apps">
          <RemovedApps />
        </ProtectedRoute>

        <ProtectedRoute path="/action-forms/new">
          <ActionFormsDetail newContent={true} />
        </ProtectedRoute>

        <ProtectedRoute path="/action-forms/:uuid">
          <ActionFormsDetail newContent={false} />
        </ProtectedRoute>

        <ProtectedRoute path="/action-forms">
          <ActionFormList />
        </ProtectedRoute>

        <ProtectedRoute
          path="/account_status/:type"
          render={(props) => {
            return <AccountStatusList {...props} />;
          }}
        />

        <ProtectedRoute path="/account_status">
          <AccountStatus />
        </ProtectedRoute>

        <ProtectedRoute
          path="/global-fields/new"
          render={(props) => {
            return <GlobalFieldsDetail {...props} newFields={true} />;
          }}
        />

        <ProtectedRoute
          path="/global-fields/:uuid"
          render={(props) => {
            return <GlobalFieldsDetail {...props} newFields={false} />;
          }}
        />

        <ProtectedRoute
          path="/global-fields"
          render={(props) => {
            return <GlobalFieldsList />;
          }}
        />

        <ProtectedRoute path="/onboarding_tracker">
          <OnboardingTracker />
        </ProtectedRoute>

        <ProtectedRoute path="/push-notifications">
          <RequireAccess
            requiredAccess={["versionizer"]}
            minVersion={9.0}
            hasMetOtherChecker={isPNAllowed}
          >
            <PushNotificationList />
          </RequireAccess>
        </ProtectedRoute>

        <ProtectedRoute path="/app-downloads">
          <RequireAccess
            requiredAccess={["versionizer"]}
            minVersion={9.0}
            hasMetOtherChecker={isPNAllowed}
          >
            <AppDownloadsList />
          </RequireAccess>
        </ProtectedRoute>

        <ProtectedRoute path="/logged-in-members">
          <RequireAccess
            requiredAccess={["versionizer"]}
            minVersion={9.0}
            hasMetOtherChecker={isPNAllowed}
          >
            <PushSubscribersList />
          </RequireAccess>
        </ProtectedRoute>

        {/* Continue this pattern for all other routes */}

        {/* For the last route */}
        <ProtectedRoute path="/" component={Home} />
      </Switch>
      </AppLayout>
    </Router>
  );

  if (Object.keys(user).length === 0) {
    return <div />;
  } else {
    return app();
  }
}

export default App;
